// src/pages/PainelAgendamentos/Detalhe/DetalheAgendamento.jsx
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  FileText,
  Building2,
  User,
  Wrench,
  MessageSquare,
  Tag,
  Link as LinkIcon,
} from "lucide-react";

export default function DetalheAgendamento({ agendamento, onClose }) {
  // 🔹 Formata data YYYY-MM-DD → DD/MM/YYYY
  const formatarData = (data) => {
    if (!data || data === "0000-00-00") return "—";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <AnimatePresence>
      {agendamento && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="bg-white rounded-lg shadow-xl w-[950px] max-h-[90vh] overflow-y-auto relative"
          >
            {/* Barra superior */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#2b2b2b] rounded-t-lg">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                📅 Detalhes do Agendamento
              </h2>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow hover:scale-110 transition-transform duration-200"
              >
                ✕
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-6 space-y-4 text-gray-700">
              {/* Linha 1 */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Calendar size={16} /> Data
                  </label>
                  <input
                    value={formatarData(agendamento.data_abertura)}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Clock size={16} /> Hora
                  </label>
                  <input
                    value={agendamento.hora_abertura || "—"}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <FileText size={16} /> Serviço
                  </label>
                  <input
                    value={agendamento.tipo_servico || "—"}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
              </div>

              {/* Linha 2 */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Building2 size={16} /> Empresa
                  </label>
                  <input
                    value={agendamento.empresa || "—"}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <User size={16} /> Colaborador
                  </label>
                  <input
                    value={agendamento.colaborador || "—"}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Wrench size={16} /> Técnico
                  </label>
                  <input
                    value={agendamento.tecnico_responsavel || "—"}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
              </div>

              {/* Observação */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                  <MessageSquare size={16} /> Observação
                </label>
                <textarea
                  value={agendamento.observacao || "Sem observação informada."}
                  readOnly
                  className="w-full border rounded-md px-2 py-2 bg-gray-100 min-h-[80px]"
                />
              </div>

              {/* Relato */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                  <MessageSquare size={16} /> Relato da Visita
                </label>
                <textarea
                  value={agendamento.relato || "Sem relato registrado."}
                  readOnly
                  className="w-full border rounded-md px-2 py-2 bg-gray-100 min-h-[80px]"
                />
              </div>

              {/* Estado e Finalizado */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Tag size={16} /> Status
                  </label>
                  <div
                    className={`px-3 py-2 rounded-md font-semibold w-full ${
                      agendamento.status?.toLowerCase().includes("conclu")
                        ? "bg-green-100 text-green-800"
                        : agendamento.status?.toLowerCase().includes("pendente")
                        ? "bg-yellow-100 text-yellow-800"
                        : agendamento.status?.toLowerCase().includes("cancel")
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {agendamento.status || "—"}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <Calendar size={16} /> Finalizado em
                  </label>
                  <input
                    value={formatarData(agendamento.finalizado_em)}
                    readOnly
                    className="w-full border rounded-md px-2 py-1 bg-gray-100"
                  />
                </div>
              </div>

              {/* Link Anexado */}
              {agendamento.linkAnexado && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    <LinkIcon size={16} /> Link Anexado
                  </label>
                  <a
                    href={agendamento.linkAnexado}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {agendamento.linkAnexado}
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
